import axios, { AxiosRequestConfig } from "axios"; // tslint:disable-line match-default-export-name
import md5 from "md5";
import { makeObservable, observable } from "mobx";

import { gGateway } from "@utils/gateway";

import { asyncTimeout } from "@use/async-timeout";
import { debounce, nonConcurrent } from "@use/decorators";
import { EntityStore } from "@use/entity-store";
import { SaveableCollection } from "@use/models";
import { hrtime } from "@use/universal-hrtime";

import { UserModel } from "@courses-models/user";

import { urls } from "@courses-models/course/src/ts/config";
import {
  BlockModel,
  BlockRemoteData,
  BlockTypes,
} from "@courses-models/course/src/ts/models/BlockModel";
import { EmbedBlockModel } from "@courses-models/course/src/ts/models/blocks/EmbedBlockModel";
import { LinkBlockModel } from "@courses-models/course/src/ts/models/blocks/LinkBlockModel";
import { ReferenceBlockModel } from "@courses-models/course/src/ts/models/blocks/ReferenceBlockModel";
import { RichtextBlockModel } from "@courses-models/course/src/ts/models/blocks/RichtextBlockModel";
import { YoutubeBlockModel } from "@courses-models/course/src/ts/models/blocks/YoutubeBlockModel";

/**
 * Memory cache of courses that have already been fetched by the client, so that
 * we don't need to fetch the same courses over and over again.
 */
const cache = new EntityStore<CourseModel>({ node: false, ttl: 600 });

/**
 * Specification of the response data format, when fetching data about a single
 * course.
 */
type GetCourseResponse = {
  objectID: string;
  created: number;
  modified: number;
  userID: string;
  title: string;
  subjects: string[];
  levels: string[];
  sharedWith?: string[];
  clonedTo?: string[];
  clonedFrom?: string;
  courseElements: BlockRemoteData[];
};

/**
 * Specification of the response when a new course is being created.
 */
type CreateResponse = {
  objectID: string;
};

/**
 * Specification of data needed to create a new course in the remote backend.
 */
export type CreateCourseData = {
  userId: string;
  userInstitutionId: string;
  title: string;
  subjects: string[];
  levels: string[];
  shared?: {
    sharedBy: string;
    clonedFrom: string;
  };
};

/**
 * Specification of data needed to update an existing course in the remote
 * backend.
 */
export type UpdateCourseData = Omit<CreateCourseData, "userInstitutionId"> & {
  id: string;
};

/**
 * Specification of data needed to instantiate a new CourseModel.
 */
export type CourseData = UpdateCourseData & {
  createdAt: number;
  modifiedAt: number;
};

/**
 * Describes a single course, exposing all methods needed to update, share and
 * delete the course.
 */
export class CourseModel extends SaveableCollection<BlockModel> {
  /**
   * Specifies the unique id of the course.
   */
  public readonly id: string;

  /**
   * Specifies the id of the user who owns / created this course.
   */
  public readonly userId: string;

  /**
   * Contains an timestamp from when this course was first created.
   */
  public readonly createdAt: Date;

  /**
   * Contains a timestamp from when this course was last modified.
   */
  public modifiedAt: Date;

  /**
   * Specifies the (plaintext) title of the course, which is used by users to
   * easily identify a course.
   */
  public title: string;

  /**
   * Specifies a list of subject names that this course covers.
   */
  public subjects: string[];

  /**
   * Specifies a list of levels that this course covers.
   */
  public levels: string[];

  /**
   * Internal collection of course blocks contained within this course.
   */
  protected collection: BlockModel[];

  /**
   * Specifies if the blocks contained within this course was given when the
   * model was instantiated.
   */
  protected readonly hasContent: boolean;

  /**
   * If this course was shared by one teacher to another teacher, then the
   * following data will be present on the course.
   */
  private readonly shared?: {
    /**
     * Specifies the id of the teacher that shared the course.
     */
    sharedBy: string;

    /**
     * Specifies the id of the course that was originally shared (this
     * instance was originally a clone of that one).
     */
    clonedFrom: string;
  };

  /**
   * Apply given data when a course is created, and make sure that it is saved
   * in our internal cache of already fetched courses for future usage.
   */
  constructor(data: CourseData, blocks?: BlockModel[]) {
    super();

    // Apply initial set of data
    this.id = data.id;
    this.userId = data.userId;
    this.createdAt = new Date(data.createdAt * 1000);
    this.modifiedAt = new Date(data.modifiedAt * 1000);
    this.title = data.title;
    this.subjects = data.subjects;
    this.levels = data.levels;
    this.shared = data.shared;
    this.collection = blocks || [];
    this.hasContent = blocks !== undefined;

    // Make sure that this course is cached!
    cache.set(this.id, this);

    makeObservable(this as unknown as CourseModel & {
      collection: BlockModel[]
    }, {
      modifiedAt: observable,
      title: observable,
      subjects: observable,
      levels: observable,
      collection: observable,
    });
  }

  /**
   * Creates a new instance of the course model, automatically checking if an
   * instance has already been cached (and in that case returning a reference
   * directly to that instance)
   */
  public static INSTANTIATE(data: CourseData): CourseModel {
    if (cache.has(data.id)) {
      return <CourseModel>cache.get(data.id);
    }

    return new CourseModel(data);
  }

  /**
   * Creates a new course based on the specified data.
   */
  public static async CREATE(data: CreateCourseData): Promise<CourseModel> {
    // Wait for the course to be created in the backend
    const response = await axios.post<CreateResponse>(
      gGateway(`${urls.userData}/v2/Courses`),
      {
        userID: data.userId,
        userInstitutionID: data.userInstitutionId,
        title: data.title,
        subjects: data.subjects,
        levels: data.levels,
        content: "",
        courseElements: [],
      },
      {
        headers: {
          "Content-Type": "application/json; charset=utf-8",
        },
      }
    );

    // Return a new course model, so that the ui can immediately interact
    // with it
    return new CourseModel({
      ...data,
      id: response.data.objectID,
      createdAt: Math.round(Date.now() / 1000),
      modifiedAt: Math.round(Date.now() / 1000),
    });
  }

  /**
   * Fetches a single course given by it's id.
   */
  @nonConcurrent()
  public static async GET_BY_ID(id: string): Promise<CourseModel> {
    // Return course from cache if it already exists there
    if (cache.has(id)) {
      return <CourseModel>cache.get(id);
    }

    // Fetch course content from the given id
    const response = await axios.get<GetCourseResponse>(
      gGateway(`${urls.userData}/v2/Courses/${id}`)
    );

    // Map data into our internal format
    const data: CourseData = {
      id: response.data.objectID,
      createdAt: response.data.created,
      modifiedAt: response.data.modified,
      userId: response.data.userID,
      title: response.data.title,
      subjects: response.data.subjects,
      levels: response.data.levels,
    };

    if (response.data.clonedFrom) {
      data.shared = {
        sharedBy: "",
        clonedFrom: response.data.clonedFrom,
      };
    }

    // Map data into our model construct
    return new CourseModel(
      data,
      response.data.courseElements.map(mapResponseToBlockModel)
    );
  }

  /**
   * Deletes the specified course entirely from the remote backend, and also
   * flushes it from the in-memory course.
   */
  public static async DELETE(course: CourseModel): Promise<CourseModel> {
    // Actually delete the request
    await axios.delete(
      gGateway(`${urls.userData}/v2/Courses?id=${encodeURIComponent(course.id)}`)
    );

    // Once the course has been deleted, make sure that it is deleted from
    // the in-memory cache
    cache.delete(course.id);

    // Return a reference to the deleted course
    return course;
  }

  /**
   * Contains the absolute url needed to open the viewer for this course.
   */
  public get url(): string {
    return urls.viewer.replace("{ id }", this.id);
  }

  /**
   * Contains the absolute url needed to open the builder for this course.
   */
  public get builderUrl(): string {
    return urls.builder.replace("{ id }", this.id);
  }

  /**
   * Helper that makes it easy to detect if the course was shared with the
   * user who fetched it
   */
  public get wasShared(): boolean {
    return !!this.shared;
  }

  /**
   * Helper that fetches data about the user that shared this course with the
   * current owner.
   */
  public async getSharedBy(): Promise<UserModel | undefined> {
    if (!this.shared || !this.shared.sharedBy) {
      return undefined;
    }

    return UserModel.GET_BY_ID(this.shared.sharedBy);
  }

  /**
   * Helper that fetches the course that this course was originally cloned
   * from when a teacher shared it with the current owner.
   */
  public async getClonedFrom(): Promise<CourseModel | undefined> {
    if (!this.shared || !this.shared.clonedFrom) {
      return undefined;
    }

    return CourseModel.GET_BY_ID(this.shared.clonedFrom);
  }

  /**
   * Helper that'll share this course (ie. create a clone of it) with the list
   * of teachers given by their ids.
   */
  public async shareWithTeachers(
    teacherIds: string[],
    delay: number = 0
  ): Promise<void> {
    return minDelay(async () => {
      await axios.post(
        gGateway(`${urls.userData}/v2/Courses/${encodeURIComponent(this.id)}/Clone`),
        JSON.stringify({
          userIDs: teacherIds,
        }),
        {
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
        }
      );
    }, delay);
  }

  /**
   * Helper that'll share this course (ie. grant access to it) with the given
   * list of students.
   */
  public async shareWithStudents(
    institutionId: string,
    studentIds: string[],
    delay: number = 0
  ): Promise<void> {
    return minDelay(async () => {
      await axios.post(
        gGateway(`${urls.userData}/v2/Courses/${encodeURIComponent(this.id)}/Share`),
        JSON.stringify({
          institutionID: institutionId,
          students: studentIds,
        }),
        {
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
        }
      );
    }, delay);
  }

  /**
   * Helper that'll generate a unique id for the next block that we're going
   * to inject into the course using timestamps and md5 hashing.
   */
  public getNextBlockId(): string {
    // We're going to generate the id based on current timstamp
    const tstamp = Date.now();
    const random = Math.random(); // tslint:disable-line insecure-random

    // Prepare initial id
    let id = md5(`${tstamp}-${random}`).substr(0, 8);
    let incrementor = 0;

    // Verify that id hasn't already been used (keep un bumping incrementor
    // by 1 until we find a truly unique id)
    while (this.getById(id) !== undefined) {
      incrementor++;
      id = md5(`${tstamp}-${random}-${incrementor}`).substr(0, 8);
    }

    // Return generated id
    return id;
  }

  /**
   * Helper that'll generate a snapshot based on the current data, which we're
   * going to use to determine if changes have been made since last save.
   */
  public getSnapshot(): string {
    return JSON.stringify({
      title: this.title,
      subjects: this.subjects,
      levels: this.levels,
      blocks: this.map((block) => block.getSnapshot()),
    });
  }

  /**
   * Overwrite the save method, making sure that the "modifiedAt" timestamp
   * will be updated.
   */
  public async save(delay?: number): Promise<void> {
    // Perform save request
    await super.save(delay);

    // Update "modifiedAt" timestamp, now that data has been saved
    this.modifiedAt = new Date();
  }

  /**
   * Exposes a method that's safe to use for auto saving data (it will perform
   * debounced save requests).
   */
  @debounce(500)
  public async autoSave(): Promise<void> {
    return this.save();
  }

  /**
   * Internal method that'll prepare the Axios request required to submit data
   * to the remote backend.
   */
  protected prepareSaveRequest(): AxiosRequestConfig {
    return {
      method: "PUT",
      url: gGateway(`${urls.userData}/v2/Courses?id=${encodeURIComponent(this.id)}`),
      data: {
        userID: this.userId,
        title: this.title,
        subjects: this.subjects,
        levels: this.levels,
        content: "",
        // tslint:disable-line no-null-keyword
        courseElements: this.hasContent
          ? this.map(mapBlockDataToRequest)
          : undefined,
      },
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
    };
  }
}

/**
 * Helper that'll perform a wrapped callback, which will not resolve the
 * external promise until after the specified delay.
 */
async function minDelay<T>(
  callback: () => Promise<T>,
  delay: number
): Promise<T> {
  // Register when the request was started
  const startedAt = hrtime();

  try {
    // Wait for the callback to finish running
    const result = await callback();

    // Wait for the given minimum duration to run out
    await asyncTimeout(Math.max(0, delay - (hrtime() - startedAt)));

    // ... And then return the result value
    return result;
  } catch (err) {
    // Wait for the given minimum duration to run out
    await asyncTimeout(Math.max(0, delay - (hrtime() - startedAt)));

    // ... Before propagating the error
    throw err;
  }
}

/**
 * Helper that'll map in-memory data to a serialized object, which can be stored
 * in the remote backend for future use.
 */
function mapBlockDataToRequest(block: BlockModel): BlockRemoteData {
  // Determine the type of the block
  let blockType: string;

  if (block instanceof EmbedBlockModel) {
    blockType = BlockTypes.EMBED;
  } else if (block instanceof LinkBlockModel) {
    blockType = BlockTypes.LINK;
  } else if (block instanceof ReferenceBlockModel) {
    blockType = BlockTypes.REFERENCE;
  } else if (block instanceof RichtextBlockModel) {
    blockType = BlockTypes.RICHTEXT;
  } else if (block instanceof YoutubeBlockModel) {
    blockType = BlockTypes.YOUTUBE;
  } else {
    throw new Error(
      "mapBlockDataToRequest(): Unable to determine type of block."
    );
  }

  return {
    id: block.id,
    title: block.title,
    type: blockType,
    note: {
      isVisible: block.note.enabled,
      content: block.note.content,
    },
    content: block.serializeContent(),
  };
}

/**
 * Helper that'll transform the serialized block data into an instance of one of
 * our block models.
 */
function mapResponseToBlockModel(data: BlockRemoteData): BlockModel {
  switch (data.type) {
    case BlockTypes.EMBED:
      return EmbedBlockModel.DESERIALIZE(data);

    case BlockTypes.LINK:
      return LinkBlockModel.DESERIALIZE(data);

    case BlockTypes.REFERENCE:
      return ReferenceBlockModel.DESERIALIZE(data);

    case BlockTypes.RICHTEXT:
      return RichtextBlockModel.DESERIALIZE(data);

    case BlockTypes.YOUTUBE:
      return YoutubeBlockModel.DESERIALIZE(data);

    default:
      throw new Error(
        "mapResponseToBlockModel(): Unable to determine type of block."
      );
  }
}
